import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { safeLocalStorage } from "../utils/safeStorage";
import { BACKEND_URL as _BACKEND_URL, API_URL as _API_URL, APP_VERSION } from "../config/backend";
import { getNativeTokenSync, loadNativeToken, setNativeToken, clearNativeToken } from "../utils/nativeAuth";
import { getAcquisition, clearAcquisition, rattacherAttribution } from "../utils/acquisition";
import { trackEvent } from "../utils/analytics";
import { memoriserConsentementOauth } from "../utils/oauthConsent";

// Source unique de l'URL backend (garde-fou app native : jamais "undefined/api").
export const BACKEND_URL = _BACKEND_URL;
export const API_URL = _API_URL;

// Web client ID Google (OAuth) — PUBLIC (pas un secret). Sert d'audience à l'ID
// token du login natif Android (Credential Manager) ET côté backend. Doit être
// IDENTIQUE à GOOGLE_CLIENT_ID côté serveur.
const GOOGLE_WEB_CLIENT_ID = "867793859001-uk4ta4cr51p967r9j1eohia3buoh2ofj.apps.googleusercontent.com";

// Instance axios unique partagée dans toute l'app
export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 15000,
});

// CSRF double-submit GLOBAL — attache automatiquement le header
// ``X-CSRF-Token`` (= valeur du cookie ``csrf_token``) à toute requête mutante.
// Avant, seul le tunnel de paiement le faisait à la main ; les actions admin
// (ex. « Marquer comme payé ») partaient sans header → rejet CSRF 403. Lecture
// inline du cookie pour éviter un import circulaire avec payments/csrf.js.
function _readCsrfCookie() {
  try {
    const m = (typeof document !== "undefined" ? document.cookie : "").match(
      /(?:^|;\s*)csrf_token=([^;]+)/
    );
    return m ? m[1] : null;
  } catch (_) {
    return null;
  }
}
// Identification du client sur CHAQUE requête (revue pair-architecte
// 06/07/2026) : X-Client (Web / Android — l'app native Capacitor embarque ce
// même bundle) + X-App-Version. Le backend n'exploite rien aujourd'hui ;
// prépare le versionnement des contrats d'API (CAS 2bis) à coût quasi nul.
api.interceptors.request.use(
  (config) => {
    try {
      config.headers = config.headers || {};
      config.headers["X-Client"] = (typeof window !== "undefined" && window.Capacitor
        && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform())
        ? "Android" : "Web";
      config.headers["X-App-Version"] = APP_VERSION;
    } catch (_) { /* jamais bloquant */ }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.request.use(
  (config) => {
    const method = (config.method || "get").toLowerCase();
    if (["post", "put", "patch", "delete"].includes(method)) {
      const token = _readCsrfCookie();
      if (token) {
        config.headers = config.headers || {};
        if (!config.headers["X-CSRF-Token"]) {
          config.headers["X-CSRF-Token"] = token;
        }
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth NATIVE par Bearer (app Android) — défense-en-profondeur : si un token a
// été stocké au login, on l'attache. Le backend l'accepte (Authorization: Bearer)
// et exempte le Bearer du CSRF. Sur le web, getNativeTokenSync() renvoie null →
// aucun header ajouté, comportement inchangé (cookies).
api.interceptors.request.use(
  (config) => {
    try {
      const t = getNativeTokenSync();
      if (t) {
        config.headers = config.headers || {};
        if (!config.headers["Authorization"]) config.headers["Authorization"] = `Bearer ${t}`;
      }
    } catch (_) { /* noop */ }
    return config;
  },
  (error) => Promise.reject(error)
);

// Charge le token natif persistant en mémoire au démarrage (no-op sur le web).
loadNativeToken();

// Refresh de session SILENCIEUX (audit iOS 06/07/2026, P1-1) : l'access-token
// expire après 24 h mais le refresh-token (30 j) n'était JAMAIS utilisé — le
// cookie était posé sur un path qui ne matchait pas l'endpoint réel, et aucun
// code front n'appelait /auth/refresh → re-login quotidien forcé. `_hadSession`
// évite d'appeler /auth/refresh pour les visiteurs anonymes (leurs 401 sur
// /auth/me sont normaux) ; `_refreshPromise` mutualise les refresh concurrents.
let _hadSession = false;
export const _markSessionActive = (v) => { _hadSession = !!v; };

// ── MUR JURIDIQUE 451 (LOT 1b, 05/09/2026) ────────────────────────────────
// Depuis #2355 le serveur REFUSE en 451 la publication et le checkout quand
// les documents acceptes ne sont plus a jour (`dependencies.py:596`). Le front
// ne connaissait pas ce code : `grep -rn "\b451\b" frontend/src/` rendait 0.
//
// L'intercepteur vit HORS de React et ne peut pas monter une modale. Il publie
// donc l'evenement ici, et `App.js` s'y abonne. Un seul abonne : la modale est
// un overlay unique, deux abonnes voudraient dire deux modales superposees.
let _legalGateListener = null;

/** S'abonne au mur juridique. Rend la fonction de desabonnement. */
export const onLegalGate = (fn) => {
  _legalGateListener = fn;
  return () => { if (_legalGateListener === fn) _legalGateListener = null; };
};

/**
 * Lit un 451 `LEGAL_UPDATE_REQUIRED` dans une erreur axios.
 *
 * Rend `{ needsAcceptance, accountType }` ou `null`. Exportee parce que les
 * pages en ont besoin AUSSI : une page qui affiche `detail` brut planterait
 * (`detail` est un OBJET sur un 451), et une page qui affiche son message
 * generique mentirait (« Verifiez votre connexion » alors que le reseau va
 * tres bien).
 */
export const readLegalGate = (error) => {
  const r = error?.response;
  if (!r || r.status !== 451) return null;
  const d = r.data?.detail;
  if (!d || typeof d !== "object" || d.code !== "LEGAL_UPDATE_REQUIRED") return null;
  return {
    needsAcceptance: Array.isArray(d.needs_acceptance) ? d.needs_acceptance : [],
    // Le serveur dit A QUI il parle : les points d'acceptation different
    // (/auth/accept-legal pour un utilisateur, /associations/auth/accept-legal
    // pour une association). Sans ce champ, la modale posterait au mauvais
    // endroit et l'acceptation ne serait jamais enregistree.
    accountType: d.account_type === "association" ? "association" : "user",
  };
};
let _refreshPromise = null;

// Retry automatique sur cold start Railway (2 tentatives avec backoff)
// Phase 4 — interceptor capte aussi le header X-Request-ID pour
// corrélation Sentry backend ↔ frontend (cf. sentryHelpers.tagRequestId).
api.interceptors.response.use(
  response => {
    try {
      // Lazy import pour ne pas créer de dépendance circulaire au boot.
      // sentryHelpers importe @sentry/react qui peut ne pas être init.
      // eslint-disable-next-line global-require
      const { tagRequestId } = require("../utils/sentryHelpers");
      tagRequestId(response);
    } catch (_) { /* no-op si helper indispo */ }
    return response;
  },
  async error => {
    const config = error.config;
    if (!config) return Promise.reject(error);
    const retryCount = config._retryCount || 0;
    const isNetworkError = error.code === "ECONNABORTED" || error.code === "ERR_NETWORK" || !error.response;
    const isServerError = error.response && error.response.status >= 502 && error.response.status <= 504;
    // Retry auto UNIQUEMENT sur méthodes idempotentes (DUPLICATE_GUARDRAILS,
    // audit 06/07/2026) : un POST coupé en vol peut avoir ABOUTI côté serveur
    // — le rejouer créerait un doublon (message, pièce jointe…). Les flux qui
    // veulent un retry de POST le gèrent explicitement (ex. AuthPages).
    const isIdempotent = ["get", "head", "options"].includes(String(config.method || "get").toLowerCase());
    if ((isNetworkError || isServerError) && isIdempotent && retryCount < 2) {
      config._retryCount = retryCount + 1;
      const delay = retryCount === 0 ? 1500 : 3000;
      await new Promise(r => setTimeout(r, delay));
      return api(config);
    }
    // 401 avec session connue → tentative unique de refresh puis rejeu.
    // Exclusions : le refresh lui-même (anti-boucle), le login (401 = mauvais
    // mot de passe, pas une session expirée) et l'espace asso (session séparée).
    const _url = String(config.url || "");
    if (
      error.response && error.response.status === 401 &&
      _hadSession && !config._authRetried &&
      !_url.includes("/auth/refresh") && !_url.includes("/auth/login") &&
      !_url.includes("/associations/auth/")
    ) {
      config._authRetried = true;
      try {
        _refreshPromise = _refreshPromise || api.post("/auth/refresh").finally(() => { _refreshPromise = null; });
        await _refreshPromise;
        return api(config);
      } catch (_) {
        _hadSession = false; // refresh KO : la session est réellement finie
      }
    }
    // Phase 4 — capte aussi le request_id sur les réponses d'erreur.
    try {
      // eslint-disable-next-line global-require
      const { tagRequestId } = require("../utils/sentryHelpers");
      tagRequestId(error.response);
    } catch (_) { /* no-op */ }
    // 451 « Unavailable For Legal Reasons » — les conditions ont change et le
    // serveur refuse d'agir. On PREVIENT sans avaler : l'erreur continue vers
    // le `catch` de l'appelant (qui doit rester libre de nettoyer son etat),
    // et la modale se monte par-dessus. Ne JAMAIS rejouer la requete ici : un
    // POST rejoue apres acceptation creerait un doublon (DUPLICATE_GUARDRAILS).
    const _gate = readLegalGate(error);
    if (_gate && _legalGateListener) {
      try { _legalGateListener(_gate); } catch (_) { /* un abonne casse ne doit pas masquer l'erreur */ }
    }
    return Promise.reject(error);
  }
);

const sendTokenToSW = (token) => {
  if (token && "serviceWorker" in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: "SET_TOKEN", token });
  }
};

/**
 * Le sondage périodique de session doit-il déconnecter l'utilisateur ?
 *
 * NON sur une panne réseau (audit 20/08/2026) : jusqu'ici, `catch(() => …)`
 * traitait TOUTE erreur comme une session expirée. Une coupure de trois
 * secondes, un 502 Railway ou un passage en tunnel déconnectaient l'interface
 * et affichaient « votre session a expiré » — message FAUX, qui fait perdre un
 * brouillon de publication en cours, éjecte du checkout, et envoie se
 * reconnecter quelqu'un dont la session est parfaitement valide.
 *
 * `e.response` absent = aucune réponse n'est jamais revenue : on ne peut RIEN
 * conclure sur l'état de la session. Un 403 ou un 500 ne disent rien non plus.
 * Seul un 401 le dit — et l'intercepteur a déjà, à ce stade, rejoué le GET deux
 * fois et tenté un refresh : y arriver signifie que la session est bien finie.
 *
 * Exporté pour être testable et prouvable par mutation.
 */
export const shouldLogoutOnPollError = (e) =>
  Boolean(e && e.response) && e.response.status === 401;

// Conversion GA4 « sign_up » des comptes Google / Apple / Microsoft (21/10/2026) :
// seule l'inscription par e-mail l'envoyait (LoginPage). Un compte créé il y a
// moins de 15 minutes, vu pour la première fois sur ce navigateur, compte comme
// une inscription ; la clé locale (posée aussi par l'inscription e-mail) évite
// tout double comptage.
export const CLE_INSCRIPTION_MESUREE = "gively_signup_tracked";
const signalerInscriptionOAuth = (u) => {
  try {
    if (!u?.created_at || localStorage.getItem(CLE_INSCRIPTION_MESUREE)) return;
    const age = Date.now() - new Date(u.created_at).getTime();
    if (!(age >= 0 && age < 15 * 60 * 1000)) return;
    localStorage.setItem(CLE_INSCRIPTION_MESUREE, "1");
    trackEvent("sign_up", { method: "oauth" });
  } catch (_) { /* une mesure ne bloque jamais la connexion */ }
};

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [legalStatus, setLegalStatus] = useState(null);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    try {
      // Auth via httpOnly cookie (set automatiquement par le navigateur)
      const response = await api.get("/auth/me");
      const { legal_status, ...userData } = response.data;
      setUser(userData);
      setLegalStatus(legal_status || null);
      _markSessionActive(true);
      rattacherAttribution((url, corps) => api.post(url, corps));
      signalerInscriptionOAuth(userData);
    } catch (e) {
      // Access-token expiré (24 h) mais refresh-cookie (30 j) peut-être encore
      // valable — cas « je reviens le lendemain » : une tentative de refresh
      // AVANT de conclure « visiteur anonyme ». Coût pour un vrai anonyme :
      // un POST /auth/refresh 401 par visite, négligeable.
      if (e?.response?.status === 401) {
        try {
          await api.post("/auth/refresh");
          const r2 = await api.get("/auth/me");
          const { legal_status: ls2, ...userData2 } = r2.data;
          setUser(userData2);
          setLegalStatus(ls2 || null);
          _markSessionActive(true);
          return;
        } catch (_) { /* vraiment pas de session */ }
      }
      setUser(null);
      setLegalStatus(null);
      _markSessionActive(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  useEffect(() => {
    if (!user) return;
    const handleVisible = () => { if (document.visibilityState === "visible") checkAuth(); };
    document.addEventListener("visibilitychange", handleVisible);
    return () => document.removeEventListener("visibilitychange", handleVisible);
  }, [user, checkAuth]);

  // Cross-tab sync via storage event (detects logout/login from other tabs instantly)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "gively_logout") {
        setUser(null);
        // P0 PII (revue Copilot #2186) — sessionStorage est PROPRE À CHAQUE
        // onglet : la purge du logout ne peut pas atteindre le brouillon des
        // autres onglets. Chaque onglet purge le sien au signal cross-tab.
        try {
          sessionStorage.removeItem("gively_phoenix_draft");
          sessionStorage.removeItem("gively_phoenix_draft_asso");
          sessionStorage.removeItem("publish_form");
          sessionStorage.removeItem("publish_step");
        } catch {}
        import("../utils/draftImages")
          .then(({ clearDraftImages }) => Promise.all([
            clearDraftImages("gively_phoenix_draft"),
            clearDraftImages("gively_phoenix_draft_asso"),
            clearDraftImages(),
          ]))
          .catch(() => {});
      }
      // R3 (#2186) : un logout ASSO dans un autre onglet purge les brouillons
      // ICI aussi (sessionStorage est par onglet) — sans toucher à la session
      // UTILISATEUR de cet onglet (signal distinct de gively_logout).
      if (e.key === "gively_asso_logout") {
        import("../utils/draftPurge")
          .then(({ purgePublishDrafts }) => purgePublishDrafts())
          .catch(() => {});
      }
      if (e.key === "gively_login") { checkAuth(); }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [checkAuth]);

  // Polling checkAuth every 5 min (only when tab is visible) to detect cookie expiry
  useEffect(() => {
    if (!user) return;
    const POLL_INTERVAL = 5 * 60 * 1000;
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        api.get("/auth/me").catch((e) => {
          if (!shouldLogoutOnPollError(e)) return;
          setUser(null);
          toast.error("Votre session a expiré. Veuillez vous reconnecter.");
        });
      }
    }, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [user]);

  const login = async (email, password, code) => {
    const { data } = await api.post("/auth/login", { email, password, code });
    // 2FA admin : le backend a envoyé un code par email et n'ouvre pas
    // encore de session. On remonte l'info au formulaire de connexion.
    if (data.requires_2fa) {
      return { requires2fa: true };
    }
    setUser(data.user);
    setLegalStatus(data.legal_status || null);
    _markSessionActive(true);
    sendTokenToSW(data.access_token);
    setNativeToken(data.access_token); // app native : Bearer (no-op web)
    return data.user;
  };

  const register = async (formData) => {
    // Attribution acquisition (first-touch, privacy-first) : rattachée au
    // compte à l'inscription puis purgée localement — jamais transmise sinon.
    let _acq = null;
    try { _acq = getAcquisition(); } catch (_) { /* noop */ }
    // « Comment avez-vous connu Gively ? » (facultatif) voyage dans l'attribution.
    const { connu_par, ...champs } = formData || {};
    if (connu_par) _acq = { ...(_acq || {}), declared: connu_par };
    const { data } = await api.post("/auth/register",
      _acq ? { ...champs, acquisition: _acq } : champs);
    try { clearAcquisition(); } catch (_) { /* noop */ }
    setUser(data.user);
    setLegalStatus(data.legal_status || null);
    _markSessionActive(true);
    sendTokenToSW(data.access_token);
    setNativeToken(data.access_token); // app native : Bearer (no-op web)
    return data.user;
  };

  const refreshLegalStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/legal-status");
      setLegalStatus(data);
    } catch { /* silent */ }
  }, []);

  const loginWithGoogle = async (opts) => {
    // #2378 — la case CGU/CGV cochée AVANT le bouton voyage jusqu'au serveur :
    // dans le POST natif, ou via sessionStorage à travers la redirection web.
    const acceptTerms = !!(opts && typeof opts === "object" && opts.acceptTerms === true);
    // App NATIVE (Android) : Google interdit l'OAuth web dans une WebView
    // (disallowed_useragent) → on passe par le login Google NATIF (Credential
    // Manager) via @capgo/capacitor-social-login, qui renvoie un ID token vérifié
    // côté backend (/auth/google/native). Le WEB garde le flux par redirection.
    let isNative = false;
    try { isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()); } catch (_) { /* web */ }

    if (isNative) {
      try {
        const { SocialLogin } = await import("@capgo/capacitor-social-login");
        await SocialLogin.initialize({ google: { webClientId: GOOGLE_WEB_CLIENT_ID, mode: "online" } });
        const res = await SocialLogin.login({ provider: "google", options: { scopes: ["email", "profile"] } });
        const idToken = res && res.result && (res.result.idToken || res.result.id_token);
        if (!idToken) throw new Error("no_id_token");
        const { data } = await api.post("/auth/google/native", { idToken, accept_terms: acceptTerms });
        // Stocke le token natif (Bearer) AVANT tout autre appel : l'auth ne
        // dépend plus du cookie tiers. Le backend renvoie session_token.
        await setNativeToken(data && (data.session_token || data.access_token));
        if (data && data.user) setUser(data.user);
        try { await checkAuth(); } catch (_) { /* le Bearer/cookie est déjà posé */ }
      } catch (e) {
        const msg = String((e && e.message) || e);
        if (!/cancel|dismiss|user_cancel|13/i.test(msg)) {
          toast.error("Connexion Google impossible sur l'app. Utilisez l'email pour l'instant.");
        }
      }
      return;
    }

    // Un blip réseau/déploiement (bascule de conteneur Railway) faisait
    // échouer ce GET idempotent avec un toast sec « Erreur connexion Google »
    // sans aucune trace exploitable (bug iOS remonté le 05/07/2026, 23:39-23:45 :
    // la fenêtre correspondait exactement à une bascule de déploiement).
    // → 1 retry automatique après 1 s, puis télémétrie via le puits de logs
    // front pour que la cause exacte (status/code) remonte dans Railway.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const { data } = await api.get("/auth/google/login");
        // Déposé juste avant de quitter la page ; relu une seule fois au retour
        // par AuthCallback (App.js) et envoyé dans /auth/google/callback.
        memoriserConsentementOauth(acceptTerms);
        window.location.href = data.auth_url;
        return;
      } catch (e) {
        if (attempt === 0) { await new Promise((r) => setTimeout(r, 1000)); continue; }
        toast.error("Erreur connexion Google — réessayez dans quelques secondes.");
        try {
          await api.post("/telemetry/frontend-log", {
            event: "AUTH_GOOGLE_LOGIN_FAILED",
            message: String((e && e.message) || e),
            extra: { status: e?.response?.status ?? "network", code: e?.code || "" },
          });
        } catch (_) { /* télémétrie best-effort */ }
      }
    }
  };

  const loginWithApple = async (opts) => {
    const acceptTerms = !!(opts && typeof opts === "object" && opts.acceptTerms === true);
    // « Sign in with Apple » — requis par la Guideline App Store 4.8 (login Google
    // présent → il faut un login équivalent qui garde l'email privé). Flux NATIF iOS
    // via @capgo/capacitor-social-login (provider apple, même plugin que Google) : on
    // récupère l'identityToken, vérifié côté backend (/auth/apple/native). Réservé à
    // l'app iOS (le web n'en a pas besoin ; 4.8 ne concerne que l'App Store).
    let isNative = false;
    try { isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()); } catch (_) { /* web */ }
    if (!isNative) {
      toast.error("La connexion Apple est disponible dans l'application iOS.");
      return;
    }
    try {
      const { SocialLogin } = await import("@capgo/capacitor-social-login");
      await SocialLogin.initialize({ apple: {} });
      const res = await SocialLogin.login({ provider: "apple", options: { scopes: ["email", "name"] } });
      const r = (res && res.result) || {};
      const profile = r.profile || {};
      const identityToken = r.identityToken || r.idToken || profile.identityToken;
      if (!identityToken) throw new Error("no_identity_token");
      // Apple ne fournit email/nom qu'au 1er consentement → on transmet ce qu'on a ;
      // le backend rattache par apple_sub (stable), jamais par le seul email.
      const payload = {
        identityToken,
        email: r.email || profile.email || undefined,
        givenName: profile.givenName || r.givenName || undefined,
        familyName: profile.familyName || r.familyName || undefined,
      };
      const { data } = await api.post("/auth/apple/native", { ...payload, accept_terms: acceptTerms });
      // Bearer natif AVANT tout autre appel (auth indépendante du cookie tiers).
      await setNativeToken(data && (data.session_token || data.access_token));
      if (data && data.user) setUser(data.user);
      try { await checkAuth(); } catch (_) { /* Bearer/cookie déjà posé */ }
    } catch (e) {
      const msg = String((e && e.message) || e);
      // Annulation utilisateur (codes Apple 1000/1001, « canceled ») → silencieux.
      if (!/cancel|dismiss|user_cancel|1000|1001/i.test(msg)) {
        toast.error("Connexion Apple impossible. Réessayez ou utilisez l'email.");
      }
    }
  };

  const logout = async () => {
    // P0 PII (audit Phoenix 23/08, ordre durci revue CodeRabbit #2186) — purge
    // du BROUILLON DE PUBLICATION EN PREMIER, avant tout await réseau : l'API
    // n'a pas de timeout (axios défaut 0), une requête qui pend ne doit jamais
    // retenir le titre, les photos (IndexedDB) ni l'ADRESSE D'EXPÉDITION du
    // compte précédent sur un appareil partagé. Les DEUX clés (user + asso).
    // R2 (#2186) : purge via la SOURCE UNIQUE bornée (utils/draftPurge) — les
    // clés sessionStorage partent immédiatement, l'IndexedDB est limité à
    // 1,5 s : un IndexedDB qui pend ne retient plus jamais la déconnexion.
    try {
      const { purgePublishDrafts } = await import("../utils/draftPurge");
      await purgePublishDrafts();
    } catch {}
    // Purge du token PUSH FCM AVANT d'invalider la session (le DELETE authentifié
    // en a besoin). Sans ça, un appareil partagé recevait encore les notifs de
    // l'ancien compte après déconnexion (audit parité Android, release v21).
    try {
      const { unregisterNativePush, isNativeSync } = await import("../native/pushNative");
      if (isNativeSync()) await unregisterNativePush(api);
    } catch {}
    // Session Stripe des composants Versements (09/10/2026) : « Only call
    // logout when your user logs out of your app » (doc Stripe, get-started-
    // connect-embedded-components). Sans attendre : Stripe injoignable ne doit
    // jamais retenir la déconnexion Gively.
    import("../components/versements/connectStripe")
      .then((m) => m.deconnecterStripeConnect())
      .catch(() => {});
    try { await api.post("/auth/logout"); } catch {}
    clearNativeToken(); // purge le token natif (Bearer)
    // GO TOTAL 09/08/2026 (P2-5) — purge des caches MESSAGERIE de l'acteur au
    // logout : sans ça, sur un appareil partagé, le compte suivant pouvait
    // afficher la liste de conversations du précédent (cache < 30 s).
    try {
      const _uid = user?.user_id;
      if (_uid) {
        localStorage.removeItem(`conversations_cache_${_uid}`);
        localStorage.removeItem(`conversations_cache_${_uid}_t`);
      }
      localStorage.removeItem("conversations_cache");
      localStorage.removeItem("conversations_cache_t");
    } catch {}
    setUser(null);
    setLegalStatus(null);
    // Notify other tabs
    safeLocalStorage.setItem("gively_logout", Date.now().toString());
    safeLocalStorage.removeItem("gively_logout");
    // Nettoyage des traces de session asso (le backend a invalidé le cookie
    // asso) + reload complet : garantit un état mémoire totalement vierge
    // (les deux contextes Auth se ré-initialisent à froid), sinon une
    // session asso résiduelle survivait en mémoire jusqu'au prochain refresh.
    try { localStorage.removeItem("asso_token"); sessionStorage.removeItem("asso_logged_in"); } catch {}
    window.location.replace("/");
  };

  const updateUser = (updated) => setUser(updated);

  return (
    <AuthContext.Provider value={{ user, loading, legalStatus, login, register, loginWithGoogle, loginWithApple, logout, updateUser, checkAuth, refreshLegalStatus }}>
      {children}
    </AuthContext.Provider>
  );
};
